'use client'

import {
  useLoadingState,
  useQueryState,
  QueryStateProvider,
} from '@dustin-web/microsite-components/src/hooks'
import { useSearchParams } from 'next/navigation'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { FilterPage } from '.'
import { getSortBy, useProductListSettings } from '@dustin-web/product-components/src/product-list'
import { useCategoryPageMetadata } from '../categories'
import { useGraphqlInfinite } from '@dustin-web/microsite-data/src/hooks/use-graphql-infinite'
import { useMicrositeSettingsContext } from '@dustin-web/microsite-components/src/hooks/use-microsite-settings'
import { dangerously_getGraphqlFetcher } from '@dustin-web/microsite-data/src/hooks/graphql-fetcher'
import {
  FacetParameterInput,
  GetCategoryForFilterPageQuery,
  GetCategoryForFilterPageQueryVariables,
} from '@productsite/src/graphql/generated/graphql-types'
import { GetCategoryForFilterPage } from '@productsite/src/graphql/get-category-for-filter-page.graphql'
import { ProductDigitalData } from '@dustin-web/product-components/src/utils/product-digital-data'
import { DigitalDataOrigin } from '@dustin-web/product-components/src/utils/digital-data-types'
import { infinityScrollPageSize } from '../constants'
import { CategoryFilterSchema } from '../schemas/collection-page-schema'
import { uniqueArray } from '../utils/unique-array'

export enum FilterPageType {
    Category = 'category',
    Campaign = 'campaign',
    NewProducts = 'newproducts'
}

export interface FilterPageMetaData {
    type: FilterPageType
    urlPartial: string
    title: string
    description: string
    seoText: string
    digitalDataOrigin: DigitalDataOrigin
    facetsFilterExpression?: string
    showOnlyUrlQuery?: string
}

type CategoryFilterPageProps = {
  categoryId: string
  pageMetaData?: FilterPageMetaData
  predefinedFacets?: FacetParameterInput[]
  initialData?: GetCategoryForFilterPageQuery | null
  hideCategoryNavigationHits?: boolean
}

const stripTrailingSlash = (path: string) => path?.replace(/\/$/, '')

const FilterPageContent = ({
  categoryId,
  pageMetaData,
  predefinedFacets,
  initialData,
  hideCategoryNavigationHits = false,
}: CategoryFilterPageProps) => {
  const { isLoading } = useLoadingState()

  useEffect(() => {
    ProductDigitalData.pushEvent('filter', 'pageView')
  }, [categoryId])

  const { sortBy } = useProductListSettings()
  const { queryState } = useQueryState()
  // When landing on page > 1, simulate the 2 auto-scroll loads that would
  // normally happen before the "Show More" button appears. Each filter page
  // chunk covers 3 logical pages (pageSize=18, 54 products). The auto-scroll
  // loads pages page+1 and page+2 before stopping, so we offset by +2 to
  // make the "Show More" URL and counter correct on direct page load.
  const [page, setPage] = useState(queryState.page > 1 ? queryState.page + 2 : queryState.page)
  const [initialPage] = useState(queryState.page)

  const { activeFilters: facets, searchwithin } = queryState

  const combinedFacets = predefinedFacets ? [...facets, ...predefinedFacets] : facets

  const variables = {
    categoryId: categoryId,
    facets: uniqueArray(combinedFacets),
    page,
    pageSize: infinityScrollPageSize,
    searchPhrase: searchwithin,
    sortBy: getSortBy(sortBy),
  }

  const { data, error, size, setSize, isValidating } = useGraphqlInfinite<
    GetCategoryForFilterPageQuery,
    GetCategoryForFilterPageQueryVariables
  >(GetCategoryForFilterPage, {
    variables,
    swrOptions: {
      initialSize: 1,
      revalidateFirstPage: false,
      ...(initialData ? { fallbackData: [initialData] } : {}),
    },
  })

  // Background fetch: when landing on page > 1, fetch previous pages
  // one at a time and store in buffer. Reveal on scroll-up.
  const micrositeSettings = useMicrositeSettingsContext()
  const fetcher = useMemo(
    () => dangerously_getGraphqlFetcher(micrositeSettings, '', {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const backgroundFetchStarted = useRef(false)
  const bufferedPagesRef = useRef<{ pageNum: number; data: GetCategoryForFilterPageQuery }[]>([])
  const [hasPreviousBuffered, setHasPreviousBuffered] = useState(false)
  const [revealedPages, setRevealedPages] = useState<GetCategoryForFilterPageQuery[]>([])
  const topSentinelRef = useRef<HTMLDivElement | null>(null)
  const productListRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (backgroundFetchStarted.current || initialPage <= 1) return
    backgroundFetchStarted.current = true
    let isCancelled = false

    const fetchPreviousPages = async () => {
      for (let p = initialPage - 1; p >= 1; p--) {
        if (isCancelled) break
        try {
          const result = await fetcher.fetchGraphql<
            GetCategoryForFilterPageQuery,
            GetCategoryForFilterPageQueryVariables
          >(GetCategoryForFilterPage, {
            categoryId,
            facets: uniqueArray(combinedFacets),
            page: p,
            pageSize: infinityScrollPageSize,
            searchPhrase: searchwithin,
            sortBy: getSortBy(sortBy),
          })
          if (isCancelled) break
          bufferedPagesRef.current.push({ pageNum: p, data: result })
          setHasPreviousBuffered(true)
        } catch (err) {
          console.error(`Error fetching filter page ${p}`, err)
          break
        }
      }
    }

    fetchPreviousPages()
    return () => { isCancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reveal buffered pages one at a time when user scrolls to top sentinel
  const isRevealingRef = useRef(false)

  useEffect(() => {
    const sentinel = topSentinelRef.current
    if (!sentinel || !hasPreviousBuffered) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isRevealingRef.current && bufferedPagesRef.current.length > 0) {
          isRevealingRef.current = true
          // Sort so the highest page number (closest to current) is revealed first
          bufferedPagesRef.current.sort((a, b) => b.pageNum - a.pageNum)
          const nextPage = bufferedPagesRef.current.shift()!
          setRevealedPages(prev => [nextPage.data, ...prev])
        }
      },
      { threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasPreviousBuffered])

  // Adjust scroll position when previous pages are revealed
  const prevRevealedCountRef = useRef(revealedPages.length)
  const lastScrollHeightRef = useRef(0)

  useLayoutEffect(() => {
    if (!isRevealingRef.current) {
      lastScrollHeightRef.current = document.documentElement.scrollHeight
      return
    }
    const newPages = revealedPages.length - prevRevealedCountRef.current
    if (newPages > 0) {
      const diff = document.documentElement.scrollHeight - lastScrollHeightRef.current
      window.scrollBy({ top: diff, behavior: 'instant' })
    }
    lastScrollHeightRef.current = document.documentElement.scrollHeight
    prevRevealedCountRef.current = revealedPages.length
    isRevealingRef.current = false
    if (bufferedPagesRef.current.length === 0) {
      setHasPreviousBuffered(false)
    }
  }, [revealedPages.length])

  // Scroll to bottom on initial load when page > 1
  const viewMoreRef = useRef<HTMLDivElement | null>(null)
  const hasScrolledRef = useRef(false)
  useEffect(() => {
    if (initialPage > 1 && viewMoreRef.current && data && !hasScrolledRef.current) {
      hasScrolledRef.current = true
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = viewMoreRef.current
          if (!el) return
          const SCROLL_OFFSET_FOR_BUTTON_VISIBILITY = 300
          const bottom = el.offsetTop + el.offsetHeight
          window.scrollTo({ top: bottom - window.innerHeight + SCROLL_OFFSET_FOR_BUTTON_VISIBILITY, behavior: 'instant' })
        })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // Merge revealed pages with current data for paginatedResults
  const paginatedResults = [...revealedPages, ...(data ?? [])]

  const category = data && data[0]?.category
  
  const { title, metaDescription } = useCategoryPageMetadata(
    category?.seoTitle ?? null,
    category?.seoMetaDescription ?? null,
    category?.seoLongTitleName ?? category?.name ?? '',
    category?.navigation?.path ?? []
  )
  const loading = isLoading || isValidating

  if (!data && loading) {
    return null
  }

  if (error) {
    throw error
  }

  const navigation = category?.navigation
  const productSearch = category?.productSearch

  if (!category || !navigation || !productSearch) {
    return null
  }

  const pathSlug = stripTrailingSlash(category.pathSlug)

  const defaultPageMetaData: FilterPageMetaData = {
    type: FilterPageType.Category,
    urlPartial: `/group/${pathSlug}`,
    title: title,
    description: metaDescription,
    seoText: category.seoText ?? '',
    digitalDataOrigin: 'filterPage'
  }

  const metaData = pageMetaData || defaultPageMetaData 

  return (
    <>
      <CategoryFilterSchema page={data} />
      <div ref={productListRef}>
        {initialPage > 1 && <div ref={topSentinelRef} style={{ height: 1 }} />}
        <FilterPage
          paginatedResults={paginatedResults}
          category={category}
          loading={loading}
          pageMetaData={metaData}
          hideCategoryNavigationHits={hideCategoryNavigationHits}
          handleLoadMore={() => {
            setSize(size + 1)
            setPage(prev => prev + 1)
          }}
          page={page}
          currentlyViewingOverride={Math.min(page * infinityScrollPageSize, productSearch.productHits)}
          viewMoreRef={viewMoreRef}
        />
      </div>
    </>
  )
}

export default function FilterPageLayout(props: CategoryFilterPageProps) {
  const searchParams = useSearchParams()
  const query = searchParams ? Object.fromEntries(searchParams.entries()) : {}

  return (
    <QueryStateProvider query={query}>
      <FilterPageContent {...props} />
    </QueryStateProvider>
  )
}
